/** @jsxImportSource @emotion/react */
import {
  Card,
  CardContent,
  Typography,
  Collapse,
  Grid,
  Divider,
  ListItem,
  ListItemIcon,
  ListItemText,
  Icon,
  Box,
  Button,
  Link,
  CardMedia,
  useTheme,
  useMediaQuery,
  IconButton,
  Popover,
} from "@mui/material"
import { CommonStyle, NftCardStyle, NftFullCardStyle } from "appearance"
import { useState } from "react"
import TierIcon from "./TierIcon"
import ExpandMore from "./ExpandMore"
import { useRouter } from "next/router"
import Carousel from "react-material-ui-carousel"
import MuiMarkdown from "mui-markdown"

type Props = {
  nft: any
  inCart: boolean
  minted?: boolean
  onAddToCart: () => void
}

const NftFullCard = ({ nft, inCart, minted, onAddToCart }: Props) => {
  const [descriptionExpanded, setDescriptionExpanded] = useState(false)
  const [itemInfoExpanded, setItemInfoExpanded] = useState(null)
  const [collectionInfoExpanded, setCollectionInfoExpandedExpanded] = useState(null)
  const theme = useTheme()
  const matches = useMediaQuery(theme.breakpoints.up("md"))

  const contentIdAttribute = nft.attributes.find(x => x.name === "CID")

  const handleDescriptionExpandClick = () => {
    setDescriptionExpanded(!descriptionExpanded)
  }

  const handleItemInfoExpandedClick = event => {
    setItemInfoExpanded(event.currentTarget)
  }

  const handleItemInfoClose = () => {
    setItemInfoExpanded(null)
  }

  const openItemInfo = Boolean(itemInfoExpanded)
  const itemInfoId = openItemInfo ? "item-info-popover" : undefined

  const handleCollectionInfoExpandedClick = event => {
    setCollectionInfoExpandedExpanded(event.currentTarget)
  }

  const handleCollectionInfoClose = () => {
    setCollectionInfoExpandedExpanded(null)
  }

  const openCollectionInfo = Boolean(collectionInfoExpanded)
  const collectionInfoId = openCollectionInfo ? "collection-info-popover" : undefined

  return (
    <Grid container sx={{ backgroundColor: "#121212", borderRadius: "5px", overflow: "hidden" }}>
      <Grid item xs={12} sx={{ display: { xs: "flex", md: "none" }, padding: 1 }}>
        <Box css={NftCardStyle.CardWrapperStyle}>
          {!matches && nft.animationUrls && nft.animationUrls.filter(x => x.value).length > 0 ? (
            <Carousel
              sx={{ paddingBottom: 4 }}
              autoPlay={false}
              indicators
              indicatorContainerProps={{
                style: {
                  position: "absolute",
                  bottom: 0,
                  zIndex: 1,
                },
              }}
              activeIndicatorIconButtonProps={{
                style: {
                  color: "#992DFC",
                },
              }}
              navButtonsAlwaysInvisible={true}
              swipe={true}>
              {nft.animationUrls
                .filter(x => x.value)
                .map((animationUrl, i) =>
                  animationUrl.type === "audio" ? (
                    <Box p={"5px"} css={CommonStyle.Square} sx={{ backgroundImage: `url(${nft.image || undefined})` }}>
                      <CardMedia component="audio" image={animationUrl.value} controls sx={{ position: "absolute", bottom: 10, left: 0, padding: 1 }} />
                    </Box>
                  ) : animationUrl.type === "video" ? (
                    <Box p={"5px"} css={CommonStyle.Square}>
                      <CardMedia sx={{ position: "absolute", top: 0, left: 0, overflow: "hidden" }}>
                        <video controls width={"100%"} height={"100%"}>
                          <source src={animationUrl.value} type="video/mp4" />
                        </video>
                      </CardMedia>
                    </Box>
                  ) : (
                    <Box p={"5px"} css={CommonStyle.Square} sx={{ backgroundImage: `url(${animationUrl.value || undefined})` }} />
                  ),
                )}
            </Carousel>
          ) : (
            <Box p={"5px"} css={CommonStyle.Square} sx={{ backgroundImage: `url(${nft.image || undefined})` }} />
          )}
        </Box>
      </Grid>
      <Grid item xs={12} md={6.5}>
        <Card css={NftFullCardStyle.CardWrapperStyle}>
          <CardContent css={NftFullCardStyle.CardContentWrapperStyle}>
            <Box css={NftFullCardStyle.CardInfoRowStyle}>
              <Box css={NftFullCardStyle.CardTitleWrapperStyle}>
                <Link variant="h4" href={`/nft/${minted ? `minted/${nft.id}` : nft.id}`} underline="none" color="white">
                  {nft.name}
                </Link>
              </Box>
              <Box css={NftFullCardStyle.TierWrapperStyle}>
                <TierIcon name={nft.tier.name} image={nft.tier.image} />
              </Box>
            </Box>
            <Divider css={NftFullCardStyle.DividerStyle} />
            <Box css={NftFullCardStyle.CardInfoColumnStyle}>
              <Collapse in={descriptionExpanded} collapsedSize={150}>
                <Box css={NftFullCardStyle.CardDescriptionWrapperStyle}>
                  <Typography variant="body2" css={NftFullCardStyle.CardDescriptionStyle}>
                    <MuiMarkdown
                      overrides={{
                        ul: {
                          props: {
                            style: {
                              color: "white",
                              fontFamily: "Poppins,Roboto",
                              fontSize: 14,
                            },
                          },
                        },
                      }}>
                      {nft.description}
                    </MuiMarkdown>
                  </Typography>
                  <br />
                </Box>
              </Collapse>
              <Box css={NftFullCardStyle.ExpandWrapperStyle}>
                <ExpandMore expand={descriptionExpanded} onClick={handleDescriptionExpandClick} aria-expanded={descriptionExpanded} aria-label="show more">
                  <Icon className="lnr-chevron-down" sx={{ fontSize: 12 }}></Icon>
                </ExpandMore>
              </Box>
            </Box>
            <Divider css={NftFullCardStyle.DividerStyle} />
            <ListItem css={NftFullCardStyle.ListItemStyle}>
              <ListItemIcon>
                <Icon className="lnr-star"></Icon>
              </ListItemIcon>
              <ListItemText primary="Item Info" />
              <IconButton onClick={handleItemInfoExpandedClick}>
                <Icon className="lnr-chevron-down" sx={{ fontSize: 12 }}></Icon>
              </IconButton>
            </ListItem>
            <Popover
              id={itemInfoId}
              open={openItemInfo}
              anchorEl={itemInfoExpanded}
              onClose={handleItemInfoClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}>
              <Box sx={{ width: 300, backgroundColor: "#121212" }}>
                <Grid container spacing={2} p={2}>
                  <Grid item xs={8}>
                    <Box>
                      <Typography css={NftFullCardStyle.PropertyLabelStyle}>Editions</Typography>
                      <Typography>{nft.currentEdition}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box>
                      <Typography css={NftFullCardStyle.PropertyLabelStyle}>{minted ? "Max supply" : "# Available"}</Typography>
                      <Typography>{minted ? nft.maxSupply : nft.currentAvailable}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={8}>
                    <Box>
                      <Typography css={NftFullCardStyle.PropertyLabelStyle}>List Date</Typography>
                      <Typography>{nft.createdAt}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box>
                      <Typography css={NftFullCardStyle.PropertyLabelStyle}>Chain</Typography>
                      <Typography>MATIC</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box>
                      <Typography css={NftFullCardStyle.PropertyLabelStyle}>CID</Typography>
                      <Link href={contentIdAttribute?.value} underline="none" target="_blank">
                        <Typography sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>{contentIdAttribute?.value}</Typography>
                      </Link>
                    </Box>
                  </Grid>
                  {minted && (
                    <>
                      <Grid item xs={12}>
                        <Box>
                          <Typography css={NftFullCardStyle.PropertyLabelStyle}>Contract Address</Typography>
                          <Typography>{nft.metadata?.contract?.address}</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box>
                          <Typography css={NftFullCardStyle.PropertyLabelStyle}>Token ID</Typography>
                          <Typography>{nft.tokenIds ? nft.tokenIds[0] : ""}</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box>
                          <Typography css={NftFullCardStyle.PropertyLabelStyle}>Token Standard</Typography>
                          <Typography>{nft.metadata?.contract?.type}</Typography>
                        </Box>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Box>
            </Popover>
            <Divider css={NftFullCardStyle.DividerStyle} />
            {/* <ListItem css={NftFullCardStyle.ListItemStyle}>
              <ListItemIcon>
                <Icon className="lnr-star"></Icon>
              </ListItemIcon>
              <ListItemText primary="Collection Info" />
              <IconButton onClick={handleCollectionInfoExpandedClick}>
                <Icon className="lnr-chevron-down" sx={{ fontSize: 12 }}></Icon>
              </IconButton>
            </ListItem>
            <Popover
              id={collectionInfoId}
              open={openCollectionInfo}
              anchorEl={collectionInfoExpanded}
              onClose={handleCollectionInfoClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}>
              <Box css={NftFullCardStyle.CardCollectionInfoWrapperStyle} sx={{ backgroundColor: "#121212" }}>
                <Typography variant="body2">{nft.collection.name}</Typography>
                <Typography variant="body2" css={NftFullCardStyle.CardDescriptionStyle}>
                  <MuiMarkdown>{nft.collection.description}</MuiMarkdown>
                </Typography>
              </Box>
            </Popover>
            <Divider css={NftFullCardStyle.DividerStyle} /> */}
            <Box css={NftFullCardStyle.CardInfoRowStyle}>
              <Box>
                <Typography css={NftFullCardStyle.PropertyLabelStyle}>Price</Typography>
                <Typography variant="h5" display="inline">
                  {nft.price}{" "}
                  <Typography css={NftFullCardStyle.CurrencyStyle} display="inline">
                    USD
                  </Typography>
                </Typography>
              </Box>
              {/* <Button disabled={inCart || nft.outOfStock || nft.burned || minted} onClick={onAddToCart}>
                <Icon sx={{ fontSize: 14, marginRight: "4px" }} className="lnr-cart"></Icon>Buy Now
              </Button> */}
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={5.5} sx={{ display: { xs: "none", md: "flex" }, padding: 1 }}>
        <Box css={NftCardStyle.CardWrapperStyle}>
          {matches && nft.animationUrls && nft.animationUrls.filter(x => x.value).length > 0 ? (
            <Carousel
              sx={{ height: "100%" }}
              autoPlay={false}
              indicators
              indicatorContainerProps={{
                style: {
                  position: "absolute",
                  bottom: 20,
                  zIndex: 1,
                },
              }}
              activeIndicatorIconButtonProps={{
                style: {
                  color: "#992DFC",
                },
              }}
              navButtonsAlwaysInvisible={true}
              swipe={true}>
              {nft.animationUrls
                .filter(x => x.value)
                .map((animationUrl, i) =>
                  animationUrl.type === "audio" ? (
                    <Box p={"5px"} css={CommonStyle.Square} sx={{ backgroundImage: `url(${nft.image || undefined})` }}>
                      <CardMedia component="audio" image={animationUrl.value} controls sx={{ position: "absolute", bottom: 10, left: 0, padding: 1 }} />
                    </Box>
                  ) : animationUrl.type === "video" ? (
                    <Box p={"5px"} css={CommonStyle.Square}>
                      <CardMedia sx={{ position: "absolute", top: 0, left: 0, overflow: "hidden" }}>
                        <video controls width={"100%"} height={"100%"}>
                          <source src={animationUrl.value} type="video/mp4" />
                        </video>
                      </CardMedia>
                    </Box>
                  ) : (
                    <Box p={"5px"} css={CommonStyle.Square} sx={{ backgroundImage: `url(${animationUrl.value || undefined})` }} />
                  ),
                )}
            </Carousel>
          ) : (
            <Box p={"5px"} css={CommonStyle.Square} sx={{ backgroundImage: `url(${nft.image || undefined})` }} />
          )}
        </Box>
      </Grid>
    </Grid>
  )
}

export default NftFullCard
